import { useState } from "react";
import { Check, Pencil, X } from "lucide-react";
import type { LeadCandidate, LeadDetail } from "@school-sales-ai/shared";
import { useCatalogContext } from "@/hooks/useCatalog";
import {
  useCreateLeadCandidate,
  useRemoveLeadCandidate,
  useUpdateLeadCandidate,
} from "@/hooks/useLeadCandidates";

/**
 * The people this lead is enquiring for — one card each, with name, age/grade
 * and their own program.
 *
 * Takes the whole `lead` and runs no query, exactly like `LeadInterestsSection`:
 * the list already arrives in the lead-detail payload.
 *
 * Renders nothing when the lead has none, which is the normal state — a prospect
 * enrolling themselves has no row here at all. The "+ Agregar" affordance still
 * shows, because a lead the AI never asked can still have children an advisor
 * learns about on a call.
 *
 * Everything is editable on purpose. The AI will occasionally register a person
 * who does not exist or spell one wrong; a card nobody can fix stops being read.
 * Every hand edit stamps `source = "human"` server-side, after which the AI can
 * only fill blanks on that row.
 */

function detailLine(c: LeadCandidate): string | null {
  const parts = [c.gradeLabel, c.age !== null ? `${c.age} años` : null].filter(
    (p): p is string => !!p,
  );
  return parts.length > 0 ? parts.join(" · ") : null;
}

export function LeadCandidatesSection({ lead }: { lead: LeadDetail }): React.JSX.Element | null {
  const [confirmingRemoveId, setConfirmingRemoveId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const { data: catalog } = useCatalogContext();

  const create = useCreateLeadCandidate(lead.id);
  const update = useUpdateLeadCandidate(lead.id);
  const remove = useRemoveLeadCandidate(lead.id);

  const candidates = lead.candidates ?? [];
  const products = catalog?.products ?? [];
  const busy = create.isPending || update.isPending || remove.isPending;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-ink-300">
          Alumnos
        </p>
        {!adding && (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="rounded-lg border border-dashed border-ink-300 px-2 py-0.5 text-[11px] font-medium text-ink-500 hover:bg-ink-050"
          >
            + Agregar
          </button>
        )}
      </div>

      {candidates.length === 0 && !adding && (
        <p className="text-xs text-ink-300">
          Sin alumnos registrados. Si el prospecto pregunta por sus hijos, el agente los registra
          aquí.
        </p>
      )}

      <div className="flex flex-col gap-2">
        {candidates.map((candidate) =>
          editingId === candidate.id ? (
            <CandidateForm
              key={candidate.id}
              candidate={candidate}
              products={products}
              busy={busy}
              onCancel={() => setEditingId(null)}
              onSubmit={(input) =>
                update.mutate(
                  { candidateId: candidate.id, input },
                  { onSuccess: () => setEditingId(null) },
                )
              }
            />
          ) : (
            <div
              key={candidate.id}
              className="flex flex-col gap-1.5 rounded-[12px] border border-ink-100 bg-white p-2.5"
            >
              <div className="flex items-start justify-between gap-2">
                <span className="min-w-0 break-words text-xs font-medium text-ink-900">
                  {candidate.name ?? "Sin nombre"}
                </span>
                <div className="flex shrink-0 items-center gap-0.5">
                  <button
                    type="button"
                    aria-label={`Editar ${candidate.name ?? "persona"}`}
                    disabled={busy}
                    onClick={() => setEditingId(candidate.id)}
                    className="rounded p-0.5 text-ink-300 hover:bg-ink-050 hover:text-ink-600 disabled:opacity-40"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    aria-label={`Quitar ${candidate.name ?? "persona"}`}
                    disabled={busy}
                    onClick={() =>
                      confirmingRemoveId === candidate.id
                        ? remove.mutate(candidate.id, {
                            onSettled: () => setConfirmingRemoveId(null),
                          })
                        : setConfirmingRemoveId(candidate.id)
                    }
                    className="rounded p-0.5 text-ink-300 hover:bg-red-50 hover:text-red-600 disabled:opacity-40"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>

              {confirmingRemoveId === candidate.id && (
                <div className="flex items-center gap-2">
                  <span className="text-[11px] text-red-600">¿Quitar a esta persona?</span>
                  <button
                    type="button"
                    onClick={() => setConfirmingRemoveId(null)}
                    className="text-[11px] font-medium text-ink-500 underline"
                  >
                    Cancelar
                  </button>
                </div>
              )}

              {detailLine(candidate) && (
                <span className="text-[11px] text-ink-500">{detailLine(candidate)}</span>
              )}

              {candidate.productName && (
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="rounded-full bg-ink-100 px-2 py-0.5 text-[10px] font-medium text-ink-600">
                    {candidate.productName}
                  </span>
                  {/* The name comes from the API join, never from the catalog
                      context — that lists active products only, so an archived
                      program would otherwise render blank. */}
                  {candidate.isArchived && (
                    <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700">
                      Archivado
                    </span>
                  )}
                </div>
              )}
            </div>
          ),
        )}

        {adding && (
          <CandidateForm
            candidate={null}
            products={products}
            busy={busy}
            onCancel={() => setAdding(false)}
            onSubmit={(input) =>
              create.mutate(
                { ...input, name: input.name ?? "" },
                { onSuccess: () => setAdding(false) },
              )
            }
          />
        )}
      </div>
    </div>
  );
}

type FormValue = {
  name?: string;
  age?: number | null;
  gradeLabel?: string | null;
  productId?: string | null;
};

/**
 * Inline add/edit. Inline rather than a modal, matching the interests picker —
 * the rail is narrow and a dialog for three fields is heavier than the edit.
 *
 * Sends only the keys that changed on an edit, because the API distinguishes an
 * absent key (leave alone) from `null` (clear).
 */
function CandidateForm({
  candidate,
  products,
  busy,
  onCancel,
  onSubmit,
}: {
  candidate: LeadCandidate | null;
  products: Array<{ id: string; name: string }>;
  busy: boolean;
  onCancel: () => void;
  onSubmit: (input: FormValue) => void;
}): React.JSX.Element {
  const [name, setName] = useState(candidate?.name ?? "");
  const [age, setAge] = useState(candidate?.age !== null && candidate?.age !== undefined ? String(candidate.age) : "");
  const [gradeLabel, setGradeLabel] = useState(candidate?.gradeLabel ?? "");
  const [productId, setProductId] = useState(candidate?.productId ?? "");

  const trimmedName = name.trim();
  const parsedAge = age.trim() === "" ? null : Number.parseInt(age, 10);
  const ageInvalid = parsedAge !== null && (Number.isNaN(parsedAge) || parsedAge < 1 || parsedAge > 99);
  const canSubmit = trimmedName.length > 0 && !ageInvalid && !busy;

  return (
    <div className="flex flex-col gap-2 rounded-[12px] border border-ink-200 bg-ink-050 p-2.5">
      <input
        autoFocus
        value={name}
        maxLength={80}
        onChange={(e) => setName(e.target.value)}
        placeholder="Nombre"
        className="w-full rounded-lg border border-ink-200 px-2 py-1.5 text-xs text-ink-900"
      />
      <div className="flex gap-2">
        <input
          value={gradeLabel}
          maxLength={60}
          onChange={(e) => setGradeLabel(e.target.value)}
          placeholder="Grado (5to de primaria)"
          className="min-w-0 flex-1 rounded-lg border border-ink-200 px-2 py-1.5 text-xs text-ink-900"
        />
        <input
          value={age}
          inputMode="numeric"
          onChange={(e) => setAge(e.target.value)}
          placeholder="Edad"
          className="w-16 shrink-0 rounded-lg border border-ink-200 px-2 py-1.5 text-xs text-ink-900"
        />
      </div>
      {ageInvalid && <span className="text-[11px] text-red-600">La edad debe ir entre 1 y 99.</span>}
      <select
        value={productId}
        onChange={(e) => setProductId(e.target.value)}
        className="w-full rounded-lg border border-ink-200 px-2 py-1.5 text-xs text-ink-900"
      >
        <option value="">Sin programa asignado</option>
        {products.map((p) => (
          <option key={p.id} value={p.id}>
            {p.name}
          </option>
        ))}
      </select>
      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="text-[11px] font-medium text-ink-500 underline"
        >
          Cancelar
        </button>
        <button
          type="button"
          disabled={!canSubmit}
          onClick={() =>
            onSubmit({
              name: trimmedName,
              age: parsedAge,
              gradeLabel: gradeLabel.trim() === "" ? null : gradeLabel.trim(),
              productId: productId === "" ? null : productId,
            })
          }
          className="inline-flex items-center gap-1 rounded-lg bg-ink-900 px-2.5 py-1 text-[11px] font-medium text-white disabled:opacity-40"
        >
          <Check className="h-3 w-3" />
          Guardar
        </button>
      </div>
    </div>
  );
}
